
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function WhoWeServePage() {
  const clientTypes = [
    {
      icon: 'ri-hospital-line',
      title: 'Hospitals & Health Systems',
      description: 'Comprehensive mobile imaging solutions for acute care hospitals, health systems, and medical centers requiring capacity expansion, renovation support, or emergency backup capabilities.',
      benefits: [
        'Rapid deployment for emergency situations',
        'Renovation and construction project support',
        'Capacity expansion without capital investment',
        'Equipment failure backup solutions',
        'Seasonal demand management'
      ],
      image: 'https://public.readdy.ai/ai/img_res/36b96332cc98f1878142d432886b6088.jpg'
    },
    {
      icon: 'ri-team-line',
      title: 'Physician Groups',
      description: 'Flexible mobile imaging services for multi-specialty physician groups, medical practices, and ambulatory care centers seeking to expand diagnostic capabilities.',
      benefits: [
        'Expand service offerings without facility investment',
        'Flexible scheduling and deployment options',
        'Revenue enhancement opportunities',
        'Patient convenience and retention',
        'Competitive advantage in local markets'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20medical%20office%20building%20with%20physician%20group%20practice%20and%20mobile%20imaging%20unit%20showing%20ambulatory%20care%20center%20and%20diagnostic%20services%20in%20modern%20healthcare%20setting%20with%20clean%20professional%20environment%20and%20simple%20neutral%20background&width=800&height=600&seq=client-physician-001&orientation=landscape'
    },
    {
      icon: 'ri-building-line',
      title: 'Imaging Centers',
      description: 'Mobile imaging solutions for outpatient imaging centers and diagnostic facilities requiring temporary capacity, equipment upgrades, or market expansion.',
      benefits: [
        'Temporary capacity during peak periods',
        'Equipment upgrade transition support',
        'Market testing for new locations',
        'Backup for equipment maintenance',
        'Cost-effective expansion strategies'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20outpatient%20imaging%20center%20facility%20exterior%20with%20diagnostic%20radiology%20services%20and%20mobile%20equipment%20deployment%20showing%20professional%20medical%20imaging%20operations%20in%20clean%20clinical%20environment%20with%20simple%20neutral%20background&width=800&height=600&seq=client-imaging-001&orientation=landscape'
    },
    {
      icon: 'ri-surgical-mask-line',
      title: 'Surgical Centers',
      description: 'Specialized mobile imaging support for ambulatory surgical centers and specialty surgery facilities requiring on-site diagnostic capabilities.',
      benefits: [
        'Pre-operative and post-operative imaging',
        'Intraoperative imaging support',
        'Enhanced patient safety protocols',
        'Reduced patient transfer requirements',
        'Improved surgical workflow efficiency'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20ambulatory%20surgical%20center%20facility%20with%20specialty%20surgery%20operations%20and%20mobile%20imaging%20equipment%20showing%20professional%20healthcare%20environment%20and%20clinical%20technology%20integration%20with%20clean%20setting%20and%20simple%20neutral%20background&width=800&height=600&seq=client-surgical-001&orientation=landscape'
    },
    {
      icon: 'ri-community-line',
      title: 'Rural & Underserved Facilities',
      description: 'Mobile imaging solutions bringing advanced diagnostic capabilities to rural hospitals, critical access hospitals, and underserved communities.',
      benefits: [
        'Access to advanced imaging technology',
        'Reduced patient travel requirements',
        'Community health improvement',
        'Cost-effective diagnostic services',
        'Compliance with quality standards'
      ],
      image: 'https://readdy.ai/api/search-image?query=Rural%20community%20hospital%20facility%20in%20countryside%20setting%20with%20mobile%20medical%20imaging%20trailer%20providing%20healthcare%20services%20to%20underserved%20area%20showing%20critical%20access%20hospital%20and%20community%20health%20support%20with%20simple%20neutral%20background&width=800&height=600&seq=client-rural-001&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <main className="pt-24 sm:pt-32 pb-12 sm:pb-20">
        {/* Hero Section */}
        <section className="px-4 sm:px-6 mb-12 sm:mb-20">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-blue text-[13px] uppercase tracking-[2px] font-medium mb-3 sm:mb-4">
              WHO WE SERVE
            </p>
            <h1 className="text-navy text-[28px] sm:text-[40px] lg:text-[48px] font-semibold leading-tight mb-4 sm:mb-6">
              Trusted by Healthcare Organizations Across California
            </h1>
            <p className="text-[#5A6C7D] text-base sm:text-lg leading-relaxed">
              Mediline Services partners with diverse healthcare organizations throughout California, providing mobile imaging solutions tailored to each facility&apos;s unique operational requirements and patient care objectives.
            </p>
          </div>
        </section>

        {/* Client Types */}
        {clientTypes.map((client, index) => (
          <section
            key={index}
            className={`px-4 sm:px-6 py-12 sm:py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F7F9FB]'}`}
          >
            <div className="max-w-7xl mx-auto">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-blue rounded-full flex items-center justify-center flex-shrink-0">
                      <i className={`${client.icon} text-white text-2xl sm:text-3xl`}></i>
                    </div>
                    <h2 className="text-navy text-[22px] sm:text-[28px] lg:text-[32px] font-semibold">
                      {client.title}
                    </h2>
                  </div>
                  <p className="text-[#5A6C7D] text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                    {client.description}
                  </p>

                  {/* Benefits */}
                  <h3 className="text-navy text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Key Benefits</h3>
                  <ul className="space-y-2 sm:space-y-3">
                    {client.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 sm:gap-3">
                        <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-check-line text-slate-blue text-lg"></i>
                        </div>
                        <span className="text-[#5A6C7D] text-sm sm:text-[15px]">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-xl h-[220px] sm:h-[320px] lg:h-[400px]">
                    <img
                      src={client.image}
                      alt={client.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Stats Section */}
        <section className="px-4 sm:px-6 py-12 sm:py-20 bg-navy">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-white text-[24px] sm:text-[30px] lg:text-[36px] font-semibold text-center mb-10 sm:mb-16">
              Our California Presence
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="text-white text-[36px] sm:text-[48px] lg:text-[56px] font-bold mb-1 sm:mb-2">340+</div>
                <div className="text-white/70 text-sm sm:text-base">Total Facilities Served</div>
              </div>
              <div className="text-center">
                <div className="text-white text-[36px] sm:text-[48px] lg:text-[56px] font-bold mb-1 sm:mb-2">15+</div>
                <div className="text-white/70 text-sm sm:text-base">Years of Experience</div>
              </div>
              <div className="text-center">
                <div className="text-white text-[36px] sm:text-[48px] lg:text-[56px] font-bold mb-1 sm:mb-2">98%</div>
                <div className="text-white/70 text-sm sm:text-base">Client Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-white text-[36px] sm:text-[48px] lg:text-[56px] font-bold mb-1 sm:mb-2">24/7</div>
                <div className="text-white/70 text-sm sm:text-base">Technical Support</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 sm:px-6 mt-12 sm:mt-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-navy to-navy-light rounded-2xl sm:rounded-3xl p-8 sm:p-12 lg:p-16 text-center">
            <h2 className="text-white text-[24px] sm:text-[30px] lg:text-[36px] font-semibold mb-3 sm:mb-4">
              Is Your Facility a Good Fit?
            </h2>
            <p className="text-white/85 text-base sm:text-lg mb-6 sm:mb-8">
              Contact our team to discuss how mobile imaging can support your organization&apos;s goals.
            </p>
            <a
              href="/contact"
              className="inline-block bg-white text-navy px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base font-semibold hover:shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Schedule Consultation
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
